import { Theme } from '@mui/material';
import classnames from 'classnames';

import { ENOPOrganType, EResourceStatus, EResourceType } from '#shared';

import { classes } from './BaseCard';

import type {
  DeviceResource,
  HumanResource,
  StaffCountsInfo,
  StaffInfo,
} from '#entities';

export const aggregateResourcesCounts = (
  staffList: StaffInfo[],
): StaffCountsInfo[] => {
  const result: Partial<Record<ENOPOrganType, StaffCountsInfo>> = {};

  for (const staff of staffList) {
    const { teamMemberType, details, countsInfo } = staff;

    // 1) Device / Specialist path: aggregate from details.resources
    if (details && details.length) {
      for (const organDetail of details) {
        const { organType, resources } = organDetail;

        if (!result[organType]) {
          result[organType] = {
            id: organDetail.id,
            organType,
            availableCount: 0,
            assignedCount: 0,
            availableTrialCount: 0,
            assignedTrialCount: 0,
          };
        }

        const acc = result[organType]!;

        for (const resource of resources ?? []) {
          // Skip if it's a HumanResource without today field
          if (teamMemberType === EResourceType.Specialist) {
            const humanResource = resource as HumanResource;
            if (!humanResource.today) {
              continue;
            }
          }

          const isAvailable = resource.status === EResourceStatus.Available;
          const isAssigned = resource.status === EResourceStatus.Assigned;

          // Check if THIS specific organ has a trial program
          let isTrialForThisOrgan = false;
          if (teamMemberType === EResourceType.Device) {
            const deviceResource = resource as DeviceResource;
            isTrialForThisOrgan =
              deviceResource.trialProgram !== null &&
              deviceResource.trialProgram.organ === organType;
          } else if (teamMemberType === EResourceType.Specialist) {
            const humanResource = resource as HumanResource;
            isTrialForThisOrgan = humanResource.trialPrograms.some(
              trial => trial.organ === organType,
            );
          }
          // Surgeon: no trial counting here

          if (isAvailable) {
            acc.availableCount += 1;
            if (isTrialForThisOrgan) {
              acc.availableTrialCount += 1;
            }
          } else if (isAssigned) {
            acc.assignedCount += 1;
            if (isTrialForThisOrgan) {
              acc.assignedTrialCount += 1;
            }
          }
        }
      }

      continue;
    }

    // 2) Surgeon aggregate from countsInfo
    if (countsInfo && countsInfo.length) {
      for (const organCount of countsInfo) {
        const { organType } = organCount;

        if (!result[organType]) {
          result[organType] = {
            id: organCount.id,
            organType,
            availableCount: 0,
            assignedCount: 0,
            availableTrialCount: 0,
            assignedTrialCount: 0,
          };
        }

        const acc = result[organType]!;

        acc.availableCount += organCount.availableCount ?? 0;
        acc.assignedCount += organCount.assignedCount ?? 0;

        // For surgeons these are always 0, but this keeps it generic
        acc.availableTrialCount += organCount.availableTrialCount ?? 0;
        acc.assignedTrialCount += organCount.assignedTrialCount ?? 0;
      }
    }
  }

  return Object.values(result);
};

export const getOrganClassName = (
  organType: ENOPOrganType,
  isEast: boolean,
): string =>
  classnames(
    classes.counts,
    isEast ? classes.countsRowEast : classes.countsRowWest,
    classes[organType],
  );

export const getAvatarBackground = (
  type: EResourceType,
  hasDots: boolean,
  theme: Theme,
): string => {
  if (!hasDots) {
    return theme.palette.grey[700];
  }
  if (type === EResourceType.Surgeon) {
    return theme.palette.error[200];
  }
  return theme.palette.mapBlue[300];
};
